'use client'
import {useState, type ReactNode} from 'react'
import {SectionAccordion} from '@fitro/customer-ui/SectionAccordion'
import {BookingRulesContent} from '@fitro/customer-ui/BookingRulesContent'
import {hasRichContent} from '@fitro/rich-content'
import {formatDateTimeLabel} from '@fitro/date-time/display'
import {useTranslation} from '../../../hooks/useTranslation'
import {SanitizedRichContent} from '../../ui/SanitizedRichContent'
import type {BookingRules} from '../../../services/booking-rules'

export function BookingRulesSection({rules, registrationClosesAt, cancellationClosesAt, cancellationFirst = false, timeZone, inline = false, children}: {
    rules?: BookingRules | null
    registrationClosesAt?: string | null
    cancellationClosesAt?: string | null
    /** Booked: the collapsed row previews the cancellation cutoff, not the booking one. */
    cancellationFirst?: boolean
    timeZone?: string
    /** A details-window row instead of a boxed disclosure. */
    inline?: boolean
    children?: ReactNode
}) {
    const {t, locale} = useTranslation('client')
    const [open, setOpen] = useState(false)
    const relative = (minutes?: number | null, after = false) => {
        if ((minutes === null || minutes === undefined) || !Number.isFinite(minutes) || minutes < 0) return null
        if (minutes === 0) return t('bookingRules.untilStart')
        const unit = minutes % 1440 === 0 ? 'days' : minutes % 60 === 0 ? 'hours' : 'minutes'
        const value = minutes / (unit === 'days' ? 1440 : unit === 'hours' ? 60 : 1)
        return t(after ? 'bookingRules.after' : 'bookingRules.before', {duration: t(`bookingRules.${unit}`, {value})})
    }
    const exact = (instant?: string | null) => instant && timeZone && Number.isFinite(Date.parse(instant))
        ? formatDateTimeLabel(instant, {locale, timeZone}) : null
    const registration = exact(registrationClosesAt) ?? (rules?.lateJoinMinutes ? relative(rules.lateJoinMinutes, true) : relative(rules?.registrationMinutes))
    const cancellation = exact(cancellationClosesAt) ?? relative(rules?.cancellationMinutes)
    const rows = [
        ...(registration ? [{id: 'registration', value: t('bookingRules.registration', {value: registration})}] : []),
        ...(cancellation ? [{id: 'cancellation', value: t('bookingRules.cancellation', {value: cancellation})}] : []),
        ...(rules?.confirmationRequired ? [{id: 'confirmation', value: t('bookingRules.confirmation')}] : []),
    ]
    const html = hasRichContent(rules?.html) ? rules?.html : null
    if (!rows.length && !html && !children) return null
    if (inline) return <SectionAccordion variant="row" title={t('bookingRules.title')} isOpen={open} onToggle={() => setOpen(value => !value)}
        description={(cancellationFirst ? rows.find(row => row.id === 'cancellation') : null)?.value ?? rows[0]?.value ?? null}>
        {open ? <BookingRulesContent rows={rows}>
            {html ? <SanitizedRichContent html={html}/> : null}
            {children}
        </BookingRulesContent> : null}
    </SectionAccordion>
    return <SectionAccordion title={t('bookingRules.title')} isOpen={open} onToggle={() => setOpen(value => !value)}>
        {open ? <BookingRulesContent rows={rows}>
            {html ? <SanitizedRichContent html={html}/> : null}
            {children}
        </BookingRulesContent> : null}
    </SectionAccordion>
}
